import {useState} from 'react'
import {useQuery, useMutation, useQueryClient} from '@tanstack/react-query'
import {Clock, RotateCcw} from 'lucide-react'
import {useAuth} from '@/contexts/auth-context'
import {Button} from '@/components/ui/button'
import {DatePicker} from '@/components/shared/DatePicker'
import {Popover, PopoverContent, PopoverTrigger} from '@/components/ui/popover'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {AppInstant, formatTimestamp} from '@/lib/date-utils'
import {permissions} from '@/lib/permissions'
import {useAppConfig} from '@/hooks/use-app-config'

export function TimeSwitch() {
  const services = useServices()
  const i18n = useI18n()
  const queryClient = useQueryClient()
  const {hasPermission} = useAuth()
  const {timeSwitchEnabled} = useAppConfig()
  const canAdmin = hasPermission(permissions.time.admin)
  const [dateTimeValue, setDateTimeValue] = useState('')

  const {data: clockStatus} = useQuery({
    queryKey: ['clock-status'],
    queryFn: () => services.clock.status(),
    refetchInterval: 10000,
    enabled: timeSwitchEnabled,
  })

  const setTimeMutation = useMutation({
    mutationFn: (time: string) => services.clock.setTime(time),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['clock-status']})
    },
  })

  const resetTimeMutation = useMutation({
    mutationFn: () => services.clock.reset(),
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['clock-status']})
    },
  })

  if (!timeSwitchEnabled || !clockStatus) {
    return null
  }

  const handleSetTime = () => {
    if (dateTimeValue) {
      setTimeMutation.mutate(dateTimeValue)
    }
  }

  const handleAdvance = (days: number) => {
    setTimeMutation.mutate(AppInstant.addDays(clockStatus.currentTime, days))
  }

  const handleReset = () => {
    resetTimeMutation.mutate()
  }

  const formattedTime = formatTimestamp(clockStatus.currentTime)

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button variant="ghost" size="icon" className="relative" aria-label={i18n.timeSwitch.timeSwitch} data-testid="btn-time-switch">
          <Clock className={`h-4 w-4 ${clockStatus.overridden ? 'text-status-warning' : ''}`}/>
          {clockStatus.overridden && (
            <span className="absolute top-1 right-1 h-2 w-2 rounded-full bg-status-warning"/>
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <div className="space-y-3">
          <div>
            <p className="text-sm font-medium">{i18n.timeSwitch.serverTime}</p>
            <p className={`text-sm ${clockStatus.overridden ? 'text-status-warning font-medium' : 'text-muted-foreground'}`}>
              {formattedTime}
            </p>
            {clockStatus.overridden && (
              <p className="text-xs text-status-warning">{i18n.timeSwitch.clockOverridden}</p>
            )}
          </div>

          {canAdmin && (
            <>
              <div className="space-y-2">
                <label htmlFor="time-switch-input" className="text-sm font-medium">{i18n.timeSwitch.setTime}</label>
                <div className="flex gap-2">
                  <DatePicker
                    testId="time-switch-input"
                    withTime
                    value={dateTimeValue}
                    onChange={setDateTimeValue}
                    ariaLabel={String(i18n.timeSwitch.setTime)}
                  />
                  <Button size="sm" onClick={handleSetTime} disabled={!dateTimeValue || setTimeMutation.isPending} data-testid="btn-set-time">
                    {i18n.common.set}
                  </Button>
                </div>
              </div>

              <div className="space-y-1">
                <p className="text-sm font-medium">{i18n.timeSwitch.quickAdvance}</p>
                <div className="flex gap-2">
                  <Button variant="outline" size="sm" onClick={() => handleAdvance(1)} disabled={setTimeMutation.isPending} data-testid="btn-advance-1-day">
                    {i18n.timeSwitch.plusOneDay}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleAdvance(7)} disabled={setTimeMutation.isPending} data-testid="btn-advance-7-days">
                    {i18n.timeSwitch.plusSevenDays}
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleAdvance(30)} disabled={setTimeMutation.isPending} data-testid="btn-advance-30-days">
                    {i18n.timeSwitch.plusThirtyDays}
                  </Button>
                </div>
              </div>

              {clockStatus.overridden && (
                <Button variant="outline" size="sm" className="w-full" onClick={handleReset} disabled={resetTimeMutation.isPending} data-testid="btn-reset-time">
                  <RotateCcw className="h-3 w-3 mr-2"/>
                  {i18n.timeSwitch.resetToSystemTime}
                </Button>
              )}
            </>
          )}
        </div>
      </PopoverContent>
    </Popover>
  )
}
